/**
 * Per-company list state (search, ordering, filters, page) kept in localStorage
 * so a list comes back the way its user left it.
 *
 * The entry is keyed per company, but the switch between companies used to leak.
 * The list kept the previous company's search and filters in component state,
 * refetched with them and — because every refetch persists what it just sent —
 * wrote them into the *new* company's entry. A workshop's care service list came
 * back empty, filtered by an organization that is not a customer of that
 * workshop, and the filter bar showed nothing at all because the id was not
 * among its options. From then on every visit to that workshop restored the
 * same poisoned entry.
 *
 * Entries written before the fix still carry those filters, so the key holds a
 * version: bumping it retires them once, everywhere, instead of asking people to
 * clear their browser storage.
 */

export const LIST_STATE_VERSION = 'v2';

// Every prefix that holds list state. An entry under one of these that does not
// carry the current version is stale, which covers both what was written before
// the version existed and whatever a later bump retires. Only list state belongs
// here — the `selected*Fields` column choices are a separate, unaffected setting.
const LIST_STATE_PREFIXES = [
  'vehicleListState:',
  'taskListState:',
  'messageListState:',
  'predictedMaintenanceListState:',
  'maintenanceReservationListState:',
  'chainPartsUsageListState:',
];

export function listStateKey(name, scope, id) {
  return `${name}:${LIST_STATE_VERSION}:${scope}:${id || 'none'}`;
}

export function loadListState(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export function saveListState(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch (e) {
    // Private mode or a full quota. The list works, it just will not be restored.
  }
}

/**
 * The state a list starts from when the selected company changes.
 *
 * A company that has no entry of its own starts clean. Falling back to what is
 * currently in state is what leaked the previous company's filters, so the only
 * value carried over is the ordering, which cannot empty a list.
 */
export function companySwitchState(savedState, currentOrdering) {
  const saved = savedState || {};
  return {
    search: saved.search || '',
    ordering: saved.ordering || currentOrdering,
    filters: saved.filters || {},
    page: saved.page || 1,
  };
}

/**
 * Drop the entries that do not carry the current version. Returns the keys it
 * removed so the cleanup can be tested; callers can ignore it.
 */
export function purgeLegacyListState(storage) {
  const store = storage || (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) {
    return [];
  }

  const versionSegment = `:${LIST_STATE_VERSION}:`;
  let stale = [];
  try {
    stale = storedKeys(store).filter(key => (
      LIST_STATE_PREFIXES.some(prefix => key.indexOf(prefix) === 0) && key.indexOf(versionSegment) === -1
    ));
    stale.forEach(key => store.removeItem(key));
  } catch (e) {
    return [];
  }
  return stale;
}

// Storage.key(i) is the interface every browser and jsdom implements; enumerating
// the object itself only works where Storage is a proxy.
function storedKeys(store) {
  if (typeof store.key !== 'function' || typeof store.length !== 'number') {
    return Object.keys(store);
  }
  const keys = [];
  for (let i = 0; i < store.length; i += 1) {
    keys.push(store.key(i));
  }
  return keys;
}
